/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import { IMAGE_PICKER_ACCEPT, validateImageFile } from "../../../utils/imageValidation";
import { getCurrencySymbol, getRestaurantCurrencyCode } from "../../../utils/currency";
import CurrencyFlag from "../AppsMenu/AppProfile/CurrencyFlag";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import { buildItemFormLivePreview } from "../Cms/MenuTree/menuTreeUtils";
import "../../../i18n";
import "./AddCategory.css";

const EMPTY_LIVE_DRAFT = { visualSettingsMode: "custom" };

const photoUrl = (photo) => `${config.UPLOADS_SERVER}/product/${photo}`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
});

const EditItem = ({ itemId, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = itemId || routeId;
  const embedded = Boolean(onClose);
  const languages = useMemo(() => getMenuLanguages(t), [t]);
  const fileInputRef = useRef(null);
  const currencyCode = getRestaurantCurrencyCode();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [itemDetails, setItemDetails] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, { itemTitle: "" }]))
  );
  const [openLangs, setOpenLangs] = useState({});
  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));

  const [price, setPrice] = useState("");
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const previewSrc =
    imageUrl || (!photoRemoved && savedPhoto ? photoUrl(savedPhoto) : null);

  const livePreview = useMemo(
    () =>
      buildItemFormLivePreview({
        titles: Object.fromEntries(
          Object.entries(itemDetails).map(([code, details]) => [
            code,
            details?.itemTitle || "",
          ])
        ),
        price,
        havePrice: true,
      }),
    [itemDetails, price]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${config.dev}/v2/items/retrieve/${id}`, {
          headers: authHeaders(),
        });
        if (cancelled) return;
        const item = response?.data;
        if (!item?._id) {
          setNotFound(true);
          return;
        }
        const details = {};
        const nextOpen = {};
        MENU_LANGUAGE_CODES.forEach((code) => {
          const langData = item.itemDetails?.find((d) => d.language === code);
          details[code] = { itemTitle: langData?.itemTitle || "" };
          if (details[code].itemTitle.trim()) nextOpen[code] = true;
        });
        setItemDetails(details);
        setOpenLangs(nextOpen);
        setPrice(item.price == null ? "" : String(item.price));
        setSavedPhoto(item.photo || null);
        setPhotoRemoved(false);
        setFile(null);
        setImageUrl(null);
      } catch (err) {
        console.error("EditItem: load failed", err);
        toast.error(t("errorTitle"));
        setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const handleLangInputChange = (lang, field, value) => {
    setItemDetails((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    const { isValid, message } = validateImageFile(selected, 5);
    if (!isValid) {
      toast.error(message);
      return;
    }
    setFile(selected);
    setImageUrl(URL.createObjectURL(selected));
    setPhotoRemoved(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    const itemDetailsToSend = Object.entries(itemDetails)
      .map(([lang, details]) => ({
        language: lang,
        itemTitle: details.itemTitle?.trim() || "",
      }))
      .filter((d) => d.itemTitle);

    if (itemDetailsToSend.length === 0) {
      swal(t("warningTitle"), t("wizardItemTitleRequired"), "warning");
      return;
    }
    if (price === "" || price === null || Number(price) < 0) {
      swal(t("warningTitle"), t("priceError"), "warning");
      return;
    }

    setSubmitting(true);
    try {
      if (file) {
        const photoData = new FormData();
        photoData.append("file", file);
        const photoRes = await axios.put(`${config.dev}/v2/items/photos/${id}`, photoData, {
          headers: authHeaders(),
        });
        if (photoRes.data?.photo) setSavedPhoto(photoRes.data.photo);
        setFile(null);
        setImageUrl(null);
        setPhotoRemoved(false);
      }

      const payload = {
        itemDetails: itemDetailsToSend,
        price: Number(price),
      };
      if (photoRemoved && !file) payload.photo = null;
      await axios.put(`${config.dev}/v2/items/edit/${id}`, payload, {
        headers: authHeaders(),
      });

      swal({
        title: t("successTitle"),
        text: t("itemUpdated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      });
      if (embedded) {
        onUpdated?.();
        onClose?.();
      } else {
        setTimeout(() => navigate(`/item-details/${id}`), 1000);
      }
    } catch (err) {
      console.error("EditItem: save failed", err);
      swal(t("errorTitle"), err?.response?.data?.message || t("updateFailed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openLanguages = languages.filter((lang) => isLangOpen(lang.code));

  const form = (
    <form
      className={`catform${embedded ? " catform--embed" : ""}`}
      onSubmit={handleSubmit}
      noValidate
    >
      {!embedded && (
        <div className="catform__top">
          <h1 className="catform__title">{t("editItem")}</h1>
          <button
            type="button"
            className="btn catform__link"
            onClick={() => navigate(`/item-details/${id}`)}
          >
            <i className="fas fa-arrow-left" aria-hidden /> {t("viewItemDetails")}
          </button>
        </div>
      )}

      <section className="catform__section">
        <h2 className="catform__heading">
          {t("prodFormPhoto")} <span className="catform__optional">{t("optional")}</span>
        </h2>
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept={IMAGE_PICKER_ACCEPT}
          onChange={handleFileChange}
        />
        <div className="catform__photo">
          <button type="button" className="catform__thumb" onClick={() => fileInputRef.current?.click()}>
            {previewSrc ? <img src={previewSrc} alt="" /> : <i className="fas fa-camera" aria-hidden />}
          </button>
          <div className="catform__photo-actions">
            <button type="button" className="btn catform__small" onClick={() => fileInputRef.current?.click()}>
              {previewSrc ? t("catFormReplacePhoto") : t("catFormChoosePhoto")}
            </button>
            {previewSrc && (
              <button
                type="button"
                className="btn catform__small is-danger"
                onClick={() => {
                  setFile(null);
                  setImageUrl(null);
                  setPhotoRemoved(true);
                }}
              >
                {t("catFormRemovePhoto")}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="catform__section">
        <h2 className="catform__heading">{t("catFormStepName")}</h2>
        <p className="catform__pills-label">{t("itemFormChooseLanguage")}</p>
        <div className="catform__pills">
          {languages.map((lang) => {
            const filled = Boolean(itemDetails[lang.code]?.itemTitle?.trim());
            return (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill ${isLangOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                {filled && <i className="fas fa-check" aria-hidden />}
              </button>
            );
          })}
        </div>
        <div className="catform__lang-fields">
          {openLanguages.map((lang) => (
            <div className="catform__lang-panel" key={lang.code} dir={lang.code === "ar" ? "rtl" : undefined}>
              <div className="catform__field">
                <label className="catform__label" htmlFor={`edit-item-title-${lang.code}`}>
                  <img src={lang.flag} alt="" />
                  {lang.label} — {t("title")}
                </label>
                <input
                  id={`edit-item-title-${lang.code}`}
                  type="text"
                  className="catform__input"
                  value={itemDetails[lang.code]?.itemTitle || ""}
                  onChange={(e) => handleLangInputChange(lang.code, "itemTitle", e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="catform__section">
        <h2 className="catform__heading">{t("prodFormPrice")}</h2>
        <div className="catform__field">
          <label className="catform__label" htmlFor="edit-item-price">
            {t("price")}
            {currencyCode && (
              <span className="catform__currency">
                <CurrencyFlag code={currencyCode} className="currency-flag--inline" />
                {currencyCode} {getCurrencySymbol(currencyCode)}
              </span>
            )}
          </label>
          <input
            id="edit-item-price"
            type="number"
            min={0}
            step="0.01"
            className="catform__input"
            value={price}
            placeholder="0.00"
            onChange={(e) => {
              const value = e.target.value.replace(",", ".");
              if (value === "" || (!Number.isNaN(Number(value)) && Number(value) >= 0)) {
                setPrice(value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") e.preventDefault();
            }}
          />
        </div>
      </section>

      <div className="catform__actions">
        <button
          type="button"
          className="btn catform__btn catform__btn--cancel"
          onClick={() => (embedded ? onClose?.() : navigate(`/item-details/${id}`))}
          disabled={submitting}
        >
          {t("cancel")}
        </button>
        <button type="submit" className="btn catform__btn catform__btn--primary" disabled={submitting || loading}>
          {submitting ? t("loading") : t("saveChanges")}
        </button>
      </div>
    </form>
  );

  if (loading) {
    if (embedded) return <p className="catform__empty">{t("loading")}…</p>;
    return (
      <>
        <PageTitle activeMenu={t("editItem")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (notFound && !embedded) {
    return (
      <>
        <PageTitle activeMenu={t("editItem")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
        </div>
      </>
    );
  }

  const preview = (
    <aside className="catform-live" aria-label={t("wizardPreviewLive")}>
      <WizardLivePhone
        draft={EMPTY_LIVE_DRAFT}
        compact
        showStepLabel={false}
        productDetail={livePreview}
      />
    </aside>
  );

  const layout = (
    <div className="catform-with-preview">
      {form}
      {preview}
    </div>
  );

  if (embedded) return layout;

  return (
    <>
      <PageTitle activeMenu={t("editItem")} motherMenu={t("kitchen")} />
      {layout}
    </>
  );
};

export default EditItem;